<template>
  <div class="flex w-72 flex-col gap-4 px-4 py-3">
    <UiComboboxMultiVee
      name="tatFilterIDs"
      :label="$t('W: Svartid')"
      :modelValue="tatIDs"
      :options="tatOptions"
      :placeholder="$t('W: Alle svartider')"
      :searchPlaceholder="$t('term.search')"
      :emptyText="$t('W: Ingen resultater')"
      @update:model-value="onTatIDsChange" />

    <UiComboboxMultiVee
      v-if="isPriorityColoringEnabled"
      name="tierFilterIDs"
      :label="$t('W: Tid tilbage')"
      :modelValue="tierIDs"
      :options="tierOptions"
      :placeholder="$t('W: Alle')"
      :searchPlaceholder="$t('term.search')"
      :emptyText="$t('W: Ingen resultater')"
      @update:model-value="onTierIDsChange" />

    <UiComboboxMultiVee
      name="insuranceCustomerAccountFilterIDs"
      :label="$t('W: Forsikringskunde')"
      :modelValue="insuranceCustomerAccountIDs"
      :options="insuranceCustomerAccountOptions"
      :placeholder="$t('W: Alle')"
      :searchPlaceholder="$t('term.search')"
      :emptyText="$t('W: Ingen resultater')"
      @update:model-value="onInsuranceCustomerAccountIDsChange" />

    <div class="flex items-center justify-between gap-8">
      <span class="text-sm">{{ $t('W: Klar til levering') }}</span>
      <button
        type="button"
        class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200"
        :class="onlyReady ? '' : 'bg-border-subtle'"
        :style="onlyReady ? 'background-color: var(--color-action)' : ''"
        @click="onToggleOnlyReady">
        <span
          class="pointer-events-none mt-0.5 inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200"
          :class="onlyReady ? 'translate-x-4' : 'translate-x-0.5'" />
      </button>
    </div>

    <div class="flex items-center justify-between border-t border-border-subtle pt-3">
      <button
        type="button"
        class="cursor-pointer text-sm text-action disabled:cursor-not-allowed disabled:opacity-40"
        :disabled="!hasActiveFilters"
        @click="onClearClick">
        {{ $t('W: Ryd filtre') }}
      </button>
      <transition
        enterActiveClass="transition-opacity duration-150"
        enterFromClass="opacity-0"
        leaveActiveClass="transition-opacity duration-500"
        leaveToClass="opacity-0">
        <span v-if="showSavedFlash" class="text-xs text-text-muted">
          {{ $t('W: Gemt') }}
        </span>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
import { useDeliveriesFilterStore } from '@/stores/deliveriesFilterStore'
import { mapActions, mapState } from 'pinia'

export default {
  name: 'OrdersDeliveriesFilterMenu',

  emits: ['close'],

  props: {
    tatOptions: {
      type: Array as () => { value: string; text: string }[],
      required: true,
    },
    tierOptions: {
      type: Array as () => { value: string; text: string }[],
      required: true,
    },
    insuranceCustomerAccountOptions: {
      type: Array as () => { value: string; text: string }[],
      required: true,
    },
    isPriorityColoringEnabled: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      showSavedFlash: false,
      savedFlashTimeout: null as ReturnType<typeof setTimeout> | null,
    }
  },

  computed: {
    ...mapState(useDeliveriesFilterStore, [
      'tatIDs',
      'tierIDs',
      'insuranceCustomerAccountIDs',
      'onlyReady',
      'hasActiveFilters',
    ]),
  },

  methods: {
    ...mapActions(useDeliveriesFilterStore, [
      'setTatIDs',
      'setTierIDs',
      'setInsuranceCustomerAccountIDs',
      'setOnlyReady',
      'clearFilters',
    ]),

    onTatIDsChange(ids: string[]) {
      this.setTatIDs(ids)
      this.flashSaved()
    },

    onTierIDsChange(ids: string[]) {
      this.setTierIDs(ids)
      this.flashSaved()
    },

    onInsuranceCustomerAccountIDsChange(ids: string[]) {
      this.setInsuranceCustomerAccountIDs(ids)
      this.flashSaved()
    },

    onToggleOnlyReady() {
      this.setOnlyReady(!this.onlyReady)
      this.flashSaved()
    },

    onClearClick() {
      this.clearFilters()
      this.flashSaved()
    },

    flashSaved() {
      this.showSavedFlash = false
      this.$nextTick(() => {
        this.showSavedFlash = true
        if (this.savedFlashTimeout) clearTimeout(this.savedFlashTimeout)
        this.savedFlashTimeout = setTimeout(() => {
          this.showSavedFlash = false
        }, 1500)
      })
    },
  },

  beforeUnmount() {
    if (this.savedFlashTimeout) clearTimeout(this.savedFlashTimeout)
  },
}
</script>
